import React, { useEffect, useState } from 'react';
import { Input, Tooltip, Button, Form } from 'antd';
import { Wrapper } from '@googlemaps/react-wrapper';
import { AimOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { InputNumber } from 'components/Input/InputNumber';
import { useStore } from '../../useStore';
import { useGpsLocation } from './useGpsLocation';
import { FormItem } from '../../../../components/Form/FormItem';
import { FORM_LAYOUT_CONFIG, GOOGLE_MAPS_API_KEY } from '../../../../consts/common';
import { DEFAULT_LAT, DEFAULT_LNG } from '../../const';
import { Map } from './Map';
import { Marker } from './Marker';
import { MapStepForm } from './slice';
import { useInventorizeStepsStore } from '../../components/store';
import { useOnlineStatus } from '../../../../hooks/useOnlineStatus';
import { MapStepContent as messages } from './messages';

export const MapStepContent: React.VFC = () => {
  const { isOnline } = useOnlineStatus();
  const [form] = Form.useForm();
  const { setNextStep } = useInventorizeStepsStore();
  const { lat, lng, setMapStepValues, commodity, newMeter } = useStore();
  const intl = useIntl();

  const [markerState, setMarkerState] = useState({
    position: { lat: lat ?? DEFAULT_LAT, lng: lng ?? DEFAULT_LNG },
  });
  const updateMarkerState = (update: typeof markerState) => setMarkerState(update);

  const { hasGeolocationAllowed, handleGetGeolocation, loadingGeolocation } = useGpsLocation(setMapStepValues);

  useEffect(() => {
    if (lat && lng) {
      form.setFieldsValue({ lat, lng });
      updateMarkerState({ position: { lat, lng } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng]);

  const handleSubmit = (values: MapStepForm) => {
    setMapStepValues(values);
    setNextStep({ newMeter, commodity });
  };

  const handleValuesChange = (changedVals: Partial<MapStepForm>) => {
    setMapStepValues(changedVals);
    updateMarkerState({ position: { ...markerState.position, ...changedVals } });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Form
        form={form}
        id="mapStepForm"
        layout="horizontal"
        name="basic"
        onFinish={handleSubmit}
        onValuesChange={handleValuesChange}
        {...FORM_LAYOUT_CONFIG}
      >
        <FormItem label={<FormattedMessage {...messages.gps} />} name="gps">
          <Input.Group style={{ display: 'flex' }}>
            <FormItem noStyle initialValue={lat} name="lat">
              <InputNumber
                placeholder={intl.formatMessage(messages.lat)}
                precision={5}
                step={0.00001}
                style={{ flexGrow: 1, marginRight: 14 }}
              />
            </FormItem>
            <FormItem noStyle initialValue={lng} name="lng">
              <InputNumber
                placeholder={intl.formatMessage(messages.lng)}
                precision={5}
                step={0.00001}
                style={{ flexGrow: 1, marginRight: 14 }}
              />
            </FormItem>

            <Tooltip
              defaultOpen
              color="red"
              open={!hasGeolocationAllowed}
              title={<FormattedMessage {...messages.geolocationNotAllowed} />}
              trigger="click"
            >
              <Button
                danger={!hasGeolocationAllowed}
                icon={<AimOutlined />}
                loading={loadingGeolocation}
                style={{ minHeight: 36, minWidth: 36 }}
                type="primary"
                onClick={handleGetGeolocation}
              />
            </Tooltip>
          </Input.Group>
        </FormItem>
      </Form>
      {isOnline && (
        <Wrapper apiKey={GOOGLE_MAPS_API_KEY}>
          <Map
            center={markerState.position}
            fullscreenControl={false}
            initialZoom={19}
            streetViewControl={false}
            style={{ flexGrow: 1, height: 300 }}
          >
            <Marker key="meterLatLng" {...markerState} />
          </Map>
        </Wrapper>
      )}
    </div>
  );
};
